import { endOfMonth, format, startOfMonth, subMonths } from "date-fns";
import { MonthlyKpiStats } from "../api/kpi";

export interface MonthlyPoint {
  month: string; // yyyy-MM
  value: number;
}

// KPI months arrive unordered; every dashboard reader wants them oldest-first.
export const toMonthlySeries = (
  stats: MonthlyKpiStats[],
  pick: (stat: MonthlyKpiStats) => number
): MonthlyPoint[] =>
  [...stats]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((stat) => ({ month: stat.month, value: pick(stat) ?? 0 }));

export const trailing = (series: MonthlyPoint[], count: number): MonthlyPoint[] =>
  count >= series.length ? series : series.slice(series.length - count);

export const latestValue = (series: MonthlyPoint[]): number | null =>
  series.length === 0 ? null : series[series.length - 1].value;

// null rather than Infinity when the baseline is zero or missing: a jump from
// nothing has no percentage, and the tile renders no delta at all in that case.
export const deltaPercent = (
  series: MonthlyPoint[]
): number | null => {
  if (series.length < 2) return null;
  const current = series[series.length - 1].value;
  const previous = series[series.length - 2].value;
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

// Fills a fixed number of slots with growing measures: candidates whose latest
// complete month rose are taken first, in the order given, so the usual set
// stays put and a slot only changes hands when its measure declines. When
// fewer than `count` are growing the rest fill in best-first, because the
// section is a fixed grid and always has slots to fill.
export const selectPositiveFirst = <
  T extends { series: MonthlyPoint[]; minBase?: number }
>(
  candidates: T[],
  count: number
): T[] => {
  const rated = candidates.map((candidate) => ({
    candidate,
    delta: deltaPercent(candidate.series),
    // The month the delta is measured from. A percentage off a base of 16 says
    // nothing about the business: one sample month goes 16 -> 1410 game plays
    // and reads +8713%, which would otherwise outrank every real measure.
    base: candidate.series[candidate.series.length - 2]?.value ?? 0,
  }));
  const isGrowing = (entry: (typeof rated)[number]) =>
    entry.delta !== null &&
    entry.delta > 0 &&
    entry.base >= (entry.candidate.minBase ?? 0);
  const growing = rated.filter(isGrowing);
  const rest = rated
    .filter((entry) => !isGrowing(entry))
    // A measure with no baseline sorts last: unknown is worse than merely down.
    .sort((a, b) => (b.delta ?? -Infinity) - (a.delta ?? -Infinity));
  return [...growing, ...rest].slice(0, count).map((entry) => entry.candidate);
};

export const EMPTY_VALUE = "—";

// Every API module hands back `response.data` untyped, so a field the backend
// does not send arrives as undefined no matter what the interface promises.
// Dashboard figures go through here instead of trusting the declared type:
// a missing figure reads as "—" rather than throwing mid-render and taking the
// whole app down with it.
export const formatOptional = (
  value: number | null | undefined,
  format: (value: number) => string
): string =>
  typeof value === "number" && Number.isFinite(value) ? format(value) : EMPTY_VALUE;

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const name = MONTH_NAMES[Number(monthNumber) - 1];
  return name ? `${name} ${year}` : month;
};

export const formatMonthTick = (month: string): string => {
  const [, monthNumber] = month.split("-");
  return MONTH_NAMES[Number(monthNumber) - 1] ?? month;
};

export const monthKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

// The nth complete month back, as the day range an API filter wants.
// monthsBack = 1 is the month that has just ended.
export const completeMonthRange = (
  today: Date,
  monthsBack: number
): { month: string; from: string; to: string } => {
  const start = startOfMonth(subMonths(today, monthsBack));
  return {
    month: monthKey(start),
    from: format(start, "yyyy-MM-dd"),
    to: format(endOfMonth(start), "yyyy-MM-dd"),
  };
};

// The running month is a partial bucket: on the 3rd it holds three days. Drawn
// as a finished month it produces a cliff, and compared against a full month it
// reports a collapse that has not happened — on 3 August the dashboard read
// -91% on transaction amount. Rows for months that have actually ended are the
// only ones a monthly total can be read from.
export const excludeCurrentMonth = <T extends { month: string }>(
  rows: T[],
  today: Date
): T[] => {
  const current = monthKey(today);
  return rows.filter((row) => row.month !== current);
};

// Names the two months a delta actually compares, so "-8%" is never mistaken
// for a comparison against the month in progress.
export const deltaLabel = (series: MonthlyPoint[]): string | null => {
  if (series.length < 2) return null;
  const current = formatMonthTick(series[series.length - 1].month);
  const previous = formatMonthTick(series[series.length - 2].month);
  return `${current} vs ${previous}`;
};
